import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import Button from './ui/button';
import heroImage from '../images/hero-image.jpg';

const Hero = () => {
  const scrollToNext = () => {
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const scrollIndicatorVariants = {
    animate: {
      y: [0, 8, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-[calc(100vh-80px)] flex flex-col items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50 z-0" />

      {/* SVG Gradient Overlay */}
      <svg
        className="absolute inset-0 w-full h-full z-5"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        viewBox="0 0 1440 560"
        preserveAspectRatio="xMidYMid slice"
        style={{ pointerEvents: 'none', opacity: 0.5 }}
      >
        <defs>
          <mask id="SvgjsMask2038">
            <rect width="1440" height="560" fill="#ffffff"></rect>
          </mask>
          <linearGradient x1="15.28%" y1="-39.29%" x2="84.72%" y2="139.29%" gradientUnits="userSpaceOnUse" id="SvgjsLinearGradient2039">
            <stop stopColor="#0e2a47" offset="0"></stop>
            <stop stopColor="rgba(231, 198, 184, 0)" offset="0.83"></stop>
          </linearGradient>
        </defs>
        <g mask="url(#SvgjsMask2038)" fill="none">
          <rect width="1440" height="560" x="0" y="0" fill="url(#SvgjsLinearGradient2039)"></rect>
          <path d="M1440 0L1082.23 0L1440 173.74z" fill="rgba(255, 255, 255, .1)"></path>
          <path d="M1082.23 0L1440 173.74L1440 352.81L746.72 0z" fill="rgba(255, 255, 255, .075)"></path>
          <path d="M746.72 0L1440 352.81L1440 474.39L394.78000000000003 0z" fill="rgba(255, 255, 255, .05)"></path>
          <path d="M394.78 0L1440 474.39L1440 519.6899999999999L252.94999999999996 0z" fill="rgba(255, 255, 255, .025)"></path>
          <path d="M0 560L554.44 560L0 494.58z" fill="rgba(0, 0, 0, .1)"></path>
          <path d="M0 494.58L554.44 560L808.3800000000001 560L0 406.64z" fill="rgba(0, 0, 0, .075)"></path>
          <path d="M0 406.64L808.3800000000001 560L1043.7400000000002 560L0 339.19z" fill="rgba(0, 0, 0, .05)"></path>
          <path d="M0 339.19L1043.7400000000002 560L1052.9200000000003 560L0 308.51z" fill="rgba(0, 0, 0, .025)"></path>
        </g>
      </svg>

      <motion.div
        className="w-full h-full px-4 sm:px-6 lg:px-8 relative z-10 flex items-center justify-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="w-full max-w-7xl">
          {/* Content */}
          <motion.div className="text-center space-y-8" variants={containerVariants}>
            <motion.div className="space-y-4" variants={itemVariants}>
              <motion.h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight"
                variants={itemVariants}
              >
                Negler som{' '}<span style={{ color: '#a8d5a8' }}>utrykker deg</span>
              </motion.h1>
              <motion.p
                className="text-lg sm:text-xl text-white max-w-xl mx-auto font-light"
                variants={itemVariants}
              >
                Opplev luksus og eleganse med våre eksklusive skjønnhetstjenester. Vi hjelper deg med å avdekke din naturlige glød.
              </motion.p>
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={itemVariants}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  size="lg"
                  variant="primary"
                  onClick={scrollToNext}
                >
                  Book tid
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Kontakt
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-20 sm:bottom-24 left-0 right-0 z-20 flex justify-center">
        <motion.button
          onClick={scrollToNext}
          className="cursor-pointer opacity-60 hover:opacity-100 transition-opacity flex justify-center items-center"
          variants={scrollIndicatorVariants}
          animate="animate"
          aria-label="Scroll to next section"
        >
          <motion.div
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            className="flex justify-center items-center"
          >
            <ChevronDown size={32} strokeWidth={1.5} className="text-Neautral-main" />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
